import { TimeValue, getTimeStringView } from "@tsp/tsp-frontend-components/tsp";

import { checkIsNotNullish } from "@tsp-core/utils/nullish";

import { CurrencyId } from "@tsp-domains/money/enums";
import { Money } from "@tsp-domains/money/types";
import { formatMoney, transformFromStorableMoney, transformToStorableMoney } from "@tsp-domains/money/utils";

import { QUANTITY_MAP_KEY_TO_ACCRUAL_MODEL_UNIT_NAME } from "../constants";
import { AccrualModelUnitName, QuantityMapKey } from "../enums";
import { Quantity, QuantityMap } from "../types";

export function transformFromBackendQuantityMap<Input extends unknown>(
  input: Input,
): Input extends QuantityMap ? QuantityMap : QuantityMap | null {
  if (checkIsNotNullish(input) && typeof input === "object") {
    if (QuantityMapKey.WORK_TOTAL_AMOUNT in input) {
      input[QuantityMapKey.WORK_TOTAL_AMOUNT] = transformFromStorableMoney(
        input[QuantityMapKey.WORK_TOTAL_AMOUNT] as number,
      );
    }

    return input as QuantityMap;
  }

  return null as any;
}

export function transformToBackendQuantityMap(quantityMap: QuantityMap): QuantityMap {
  if (QuantityMapKey.WORK_TOTAL_AMOUNT in quantityMap) {
    quantityMap[QuantityMapKey.WORK_TOTAL_AMOUNT] = transformToStorableMoney(
      quantityMap[QuantityMapKey.WORK_TOTAL_AMOUNT],
    ) as unknown as Quantity;
  }

  return quantityMap;
}

function formatSecondsQuantity(quantity: Quantity): string {
  const time: TimeValue = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: quantity ?? 0,
    milliseconds: 0,
  };

  return getTimeStringView(time, "seconds");
}

function formatMillisecondsQuantity(quantity: Quantity): string {
  const time: TimeValue = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: quantity ?? 0,
  };

  return getTimeStringView(time, "milliseconds");
}

export function formatQuantity(
  quantity: Quantity,
  key: QuantityMapKey,
  unitName: AccrualModelUnitName,
  currencyId: CurrencyId,
): string {
  if (key === QuantityMapKey.QUANTITY || key === QuantityMapKey.EMPTY) {
    if (unitName === AccrualModelUnitName.SECOND) {
      return formatSecondsQuantity(quantity);
    }

    if (unitName === AccrualModelUnitName.MILLISECOND) {
      return formatMillisecondsQuantity(quantity);
    }

    return `${quantity}`;
  }

  if (QUANTITY_MAP_KEY_TO_ACCRUAL_MODEL_UNIT_NAME[key] === AccrualModelUnitName.SECOND) {
    return formatSecondsQuantity(quantity);
  }

  if (QUANTITY_MAP_KEY_TO_ACCRUAL_MODEL_UNIT_NAME[key] === AccrualModelUnitName.MILLISECOND) {
    return formatMillisecondsQuantity(quantity);
  }

  if (QUANTITY_MAP_KEY_TO_ACCRUAL_MODEL_UNIT_NAME[key] === AccrualModelUnitName.AMOUNT) {
    return formatMoney(quantity as unknown as Money, currencyId);
  }

  return `${quantity}`;
}
